import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { Katalog, SkillWithRating } from '../../../../shared/model/katalog.model';
import {
  applyRatings,
  CatalogAssignment,
  toAssignment,
} from '../../../../shared/model/catalog-assignment.model';
import { BeraterService } from '../../../../services/berater.service';
import { MessageService } from '../../../../shared/components/message.service';
import { Berater } from '../../../../shared/model/berater.model';
import {
  MatCell,
  MatCellDef,
  MatColumnDef,
  MatHeaderCell,
  MatHeaderCellDef,
  MatHeaderRow,
  MatHeaderRowDef,
  MatRow,
  MatRowDef,
  MatTable,
  MatTableDataSource
} from '@angular/material/table';
import { MatButton, MatIconButton } from '@angular/material/button';
import { TranslatePipe } from '@ngx-translate/core';
import { MatSort, MatSortHeader } from '@angular/material/sort';
import { MatCard, MatCardContent } from '@angular/material/card';
import { MatIcon } from '@angular/material/icon';

@Component({
  selector: 'app-skills-ba',
  imports: [
    MatHeaderRow,
    MatRow,
    MatRowDef,
    MatHeaderRowDef,
    MatHeaderCell,
    MatCell,
    MatColumnDef,
    MatTable,
    MatButton,
    MatCellDef,
    MatHeaderCellDef,
    TranslatePipe,
    MatSort,
    MatSortHeader,
    MatCard,
    MatCardContent,
    MatIconButton,
    MatIcon,
  ],
  templateUrl: './skills-ba.component.html',
  styleUrl: './skills-ba.component.scss',
  standalone: true,
})
export class SkillsBaComponent implements OnInit, OnChanges {
  @Input() consultant: Berater;
  @Input() catalog: Katalog;
  @Input() exportMode: boolean = false;

  /**
   * NP20-911: Der vom Backend gelieferte Stand geht nach oben, damit der Parent ihn uebernimmt
   * und seine Zaehler neu berechnet — wie bei Personendaten, Ausbildung, Werdegang und Schulungen.
   * NP20-889: Das PATCH liefert die Katalog-Zuweisungen, nicht mehr den ganzen Berater.
   */
  @Output() saved = new EventEmitter<CatalogAssignment[]>();

  private _sort: MatSort | null = null;

  @ViewChild(MatSort)
  set sort(sort: MatSort | null) {
    this._sort = sort;

    if (this._sort) {
      this.dataSource.sort = this._sort;

      Promise.resolve().then(() => this.applyInitialSort());
    }
  }

  get sort(): MatSort | null {
    return this._sort;
  }

  editMode: boolean = false;
  /** NP20-822: Laeuft gerade ein Speichern, loest ein weiterer Klick keinen zweiten Request aus. */
  isSaving = false;
  displayedColumns: string[] = ['skill', 'level'];
  originalCatalog: Katalog;

  dataSource = new MatTableDataSource<SkillWithRating>();

  constructor(
    private beraterService: BeraterService,
    private messageService: MessageService,
  ) {}

  ngOnInit(): void {
    this.dataSource.sortingDataAccessor = (
      item: SkillWithRating,
      property: string,
    ) => {
      switch (property) {
        case 'skill':
          return (item.bezeichnung ?? '').toLocaleLowerCase('de-DE');
        case 'level':
          return this.levelToStars(item.bewertung);
        default:
          return '';
      }
    };
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['catalog'] && this.catalog?.kenntnisse) {
      this.refreshDataSource();
    }
  }

  private applyInitialSort(): void {
    if (!this.sort) {
      return;
    }

    const data = this.dataSource.data ?? [];
    if (!data.length) {
      return;
    }

    if (this.sort.active && this.sort.direction) {
      return;
    }

    const hasMissingLevels = data.some((item) => this.isUnrated(item));

    const activeId = hasMissingLevels ? 'level' : 'skill';

    this.sort.sort({
      id: activeId,
      start: 'asc',
      disableClear: true,
    });
  }

  private refreshDataSource(): void {
    this.dataSource.data = [...(this.catalog?.kenntnisse ?? [])];

    if (this.sort) {
      this.dataSource.sort = this.sort;
      this.applyInitialSort();
    }
  }

  save() {
    if (this.isSaving) return;
    this.isSaving = true;
    // NP20-889: Nur der bearbeitete Katalog geht raus. Das PATCH ist ein Upsert pro Katalog —
    // die uebrigen Zuweisungen des Beraters bleiben unberuehrt, ohne dass sie mitgeschickt werden.
    this.beraterService
      .upsertCatalogAssignments(this.consultant.id, [toAssignment(this.catalog)])
      .subscribe({
        next: (assignments) => {
          this.isSaving = false;
          // NP20-915: erst jetzt gilt gespeichert.
          this.editMode = false;
          // Ohne Guard riss die Ansicht auf, falls die Antwort den gerade gespeicherten Katalog
          // nicht enthaelt; dann bleibt der bearbeitete Stand sichtbar.
          const gespeichert = assignments.find((a) => a.catalogId === this.catalog.id);
          if (gespeichert) {
            this.catalog = applyRatings(this.catalog, gespeichert.ratings ?? []);
          }
          this.refreshDataSource();
          this.saved.emit(assignments);
        },
        error: () => {
          this.isSaving = false;
          // NP20-915: Bearbeitungsmodus bleibt offen, die Bewertung steht weiter in der Tabelle.
          this.messageService.error('baCatalog.skillBa.errorMessage');
        },
      });
  }

  isUnrated(item: SkillWithRating): boolean {
    return !item?.bewertung;
  }

  levelToStars(level: number | null): number {
    if (level === null) return 0;
    return level;
  }

  setLevelByStars(item: SkillWithRating, stars: number): void {
    this.setActive(item, stars);
  }

  levelLabel(level: number | null): string {
    switch (level) {
      case 1:
        return 'NOTHING';
      case 2:
        return 'BASIC';
      case 3:
        return 'ADVANCED';
      case 4:
        return 'SPECIAL';
      default:
        return 'null';
    }
  }

  setActive(item: SkillWithRating, level: number) {
    item.bewertung = level;
  }

  onToggleEdit(): void {
    if (this.editMode) {
      this.save();
    } else {
      this.originalCatalog = JSON.parse(JSON.stringify(this.catalog));
      // NP20-912: Ab hier auf einer eigenen Kopie arbeiten. Die Bewertung schrieb sonst direkt in
      // die Objekte des Profils, und Abbrechen tauschte nur die Anzeige zurueck.
      this.catalog = JSON.parse(JSON.stringify(this.catalog));
      this.editMode = true;
      this.refreshDataSource();
    }
  }

  onCancelEdit() {
    this.catalog = JSON.parse(JSON.stringify(this.originalCatalog));
    this.editMode = false;
    this.refreshDataSource();
  }
}
